import { ProfileFontSet, ProfileUserResponse, UserResponse } from "@authory-packages/core";
import { V3Dropdown } from "../Dropdown";
import { FakeProfileSetSelect } from "../FakeProfileSetSelect";
import { V3InputLabel } from "../SharedInputComponent";
import { StickyBottomBar } from "../StickyBottomBar";
import { VerticalSpacing } from "../VerticalSpacing";
import { BrandingOptionsBottomBarWrapper, PreloadFontHeading, PreloadFontParagraph } from "./BrandingBar.styles";
import { useThemeOverride } from "../../theme";
import { useFormik } from "formik";
import { useTheme } from "styled-components";
import * as Yup from "yup";
import { ColorHexCustomizeValidation } from "../../shared-logic";
import { ColorSwatch, InputWithColorSwatch } from "../ColorSwatch";
import { V3BodyGrey48 } from "../ReusableStyledComponents";
import { InlineLink } from "../ReusableStyledComponents/InlineLink";
import { useEffect, useState } from "react";
import { useWarnIfUnsavedChanges } from "../../hooks/confirmNavigation";
import { WrapperWithTooltip } from "../ReusableStyledComponents/WrapperWithTooltip";
import { V3Tooltip } from "../Tooltip";
import { V3IconHelpCircle } from "../Icons";

export const BACKGROUND_COLORS = [
    "#FFFFFF",
    "#FAFAFA",
    "#FFF1F2",
    "#EEF2FF",
    "#F0FDFA",
    "#FFF7ED",
    "#f0f9ff",
    "#292524",
    "#171717",
    "#221A4D",
    "#042F2E",
    "#111827"
];
const ACCENT_COLORS = [
    "#2E77FF",
    "#E11D48",
    "#EC4899",
    "#C026D3",
    "#9333EA",
    "#F4C15D",
    "#4f46E5",
    "#0284C7",
    "#0D9488",
    "#16A34A",
    "#EA580C",
    "#221A4D"
];
export interface CustomizationProps {
    profileFontSet: ProfileFontSet;
    backgroundColor: string;
    accentcolour: string;
}

export interface BrandingBarProps {
    author: UserResponse | ProfileUserResponse;
    onCancelHandler: () => void;
    onConfirmHandler: (data: CustomizationProps) => void;
}

export const BrandingBar = ({ author, onCancelHandler, onConfirmHandler }: BrandingBarProps) => {
    const { setThemeOverride } = useThemeOverride();
    const theme = useTheme();
    const [skipWarn, setSkipWarn] = useState(false);

    const fontSetFormik = useFormik<{ value: ProfileFontSet }>({
        initialValues: {
            value: theme.customization.profileFontSet || ""
        },
        onSubmit: async () => {},
        enableReinitialize: false
    });

    const backgroundColorformik = useFormik<{ color: string }>({
        initialValues: {
            color: theme.customization.backgroundColor || ""
        },
        validationSchema: Yup.object().shape({
            color: ColorHexCustomizeValidation
        }),
        onSubmit: async () => {}
    });

    const accentColorformik = useFormik<{ color: string }>({
        initialValues: {
            color: theme.customization.accentcolour || ""
        },
        validationSchema: Yup.object().shape({
            color: ColorHexCustomizeValidation
        }),
        onSubmit: async () => {}
    });

    useWarnIfUnsavedChanges((accentColorformik.dirty || backgroundColorformik.dirty || fontSetFormik.dirty) && !skipWarn);

    useEffect(() => {
        if (window.Intercom) {
            window.Intercom("update", { hide_default_launcher: true });
        }
    }, [accentColorformik.values, backgroundColorformik.values, fontSetFormik.values]);

    return (
        <StickyBottomBar
            useMobileModal
            mobileModaltile="Branding"
            topBarTitle="Branding"
            topBarContent={
                <>
                    <V3BodyGrey48>
                        Customize fonts and colors to match your personal brand.{" "}
                        <InlineLink
                            href="https://authory.com/resources/tutorials/how-to-customize-your-authory-portfolio-layout-2#branding-fonts-colors-and-visual-identity"
                            target="_blank"
                        >
                            Learn more
                        </InlineLink>
                    </V3BodyGrey48>
                </>
            }
            leftSideElement={
                <BrandingOptionsBottomBarWrapper>
                    <div>
                        <VerticalSpacing bottom={6}>
                            <WrapperWithTooltip>
                                <div>
                                    <V3InputLabel>
                                        <strong>Fonts</strong>
                                    </V3InputLabel>
                                </div>
                                <div>
                                    <V3Tooltip
                                        tooltipMaxWidth={240}
                                        placement="right"
                                        referenceContent={<V3IconHelpCircle />}
                                        tooltipContent={
                                            "Choose the fonts used for headings and body text across your portfolio, collections and work ."
                                        }
                                    />
                                </div>
                            </WrapperWithTooltip>
                        </VerticalSpacing>
                        {Object.values(ProfileFontSet).map((pf) => (
                            <>
                                <PreloadFontHeading $fontFamily={pf}>.</PreloadFontHeading>
                                <PreloadFontParagraph $fontFamily={pf}>.</PreloadFontParagraph>
                            </>
                        ))}
                        <V3Dropdown
                            customOffset={10}
                            maxWidth={380}
                            placement="bottom-start"
                            renderReferenceElement={() => <FakeProfileSetSelect profileFontset={fontSetFormik.values.value} />}
                            renderPopperElement={(_, setShowPopper) => {
                                return (
                                    <>
                                        {Object.values(ProfileFontSet).map((pf) => (
                                            <div
                                                onClick={() => {
                                                    setShowPopper(false);
                                                    setThemeOverride({
                                                        customization: {
                                                            profileFontSet: pf
                                                        }
                                                    });
                                                    fontSetFormik.setFieldValue("value", pf);
                                                }}
                                            >
                                                <FakeProfileSetSelect profileFontset={pf} isOption />
                                            </div>
                                        ))}
                                    </>
                                );
                            }}
                        />
                    </div>
                    <div>
                        <V3Dropdown
                            onShowChange={(show) => {
                                if (!show && backgroundColorformik.isValid) {
                                    setThemeOverride({
                                        customization: {
                                            backgroundColor: backgroundColorformik.values.color
                                        }
                                    });
                                }
                            }}
                            renderPopperElement={(_, setOpen) => {
                                return (
                                    <ColorSwatch
                                        allowColorPicker={false}
                                        formik={backgroundColorformik}
                                        colors={BACKGROUND_COLORS}
                                        onChange={async (_) => {
                                            await backgroundColorformik.validateForm();
                                            setOpen(false);
                                        }}
                                    />
                                );
                            }}
                            renderReferenceElement={() => {
                                return (
                                    <InputWithColorSwatch
                                        text={theme.customization.backgroundColor}
                                        label="Background color"
                                        tooltipContent={
                                            <>
                                                Choose the background color for your portfolio, collections and work. The cover can be set separately.
                                            </>
                                        }
                                        tooltipMaxWidth={230}
                                        hasContentSelected={false}
                                    />
                                );
                            }}
                        />
                    </div>
                    <div>
                        <V3Dropdown
                            onShowChange={(show) => {
                                if (!show && accentColorformik.isValid) {
                                    setThemeOverride({
                                        customization: {
                                            accentcolour: accentColorformik.values.color
                                        }
                                    });
                                }
                            }}
                            renderPopperElement={(_, setOpen) => {
                                return (
                                    <ColorSwatch
                                        allowColorPicker={false}
                                        formik={accentColorformik}
                                        colors={ACCENT_COLORS}
                                        onChange={async (_) => {
                                            await accentColorformik.validateForm();
                                            setOpen(false);
                                        }}
                                    />
                                );
                            }}
                            renderReferenceElement={() => {
                                return (
                                    <InputWithColorSwatch
                                        tooltipContent={"Choose the accent color for your portfolio, collections and work."}
                                        text={theme.customization.accentcolour}
                                        hasContentSelected={false}
                                        tooltipMaxWidth={220}
                                        label="Accent color"
                                    />
                                );
                            }}
                        />
                    </div>
                </BrandingOptionsBottomBarWrapper>
            }
            rightSidebuttonConfirmLabel={"Save"}
            onCancelHandler={() => {
                setSkipWarn(true);
                setThemeOverride({
                    customization: {
                        profileFontSet: author.profileFontSet,
                        backgroundColor: author.backgroundColor!,
                        accentcolour: author.accentcolour!
                    }
                });
                onCancelHandler();
            }}
            onConfirmHandler={() => {
                setSkipWarn(true);
                onConfirmHandler({
                    profileFontSet: fontSetFormik.values.value,
                    backgroundColor: backgroundColorformik.values.color,
                    accentcolour: accentColorformik.values.color
                });
            }}
            maxWidth={1060}
            isSubmitting={false}
        />
    );
};
